/**
 * exportFonts — collecte les polices SYSTÈME à acheminer au service de rendu.
 *
 * Les polices Google et bundlées sont chargées par /print (via <link> Google
 * Fonts). Le seul cas à gérer ici : les polices SYSTÈME de l'utilisateur
 * (choisies via window.queryLocalFonts) — le serveur Linux ne les possède pas.
 * On récupère donc leur fichier (.blob()) côté navigateur et on l'envoie au
 * service, qui l'injecte en @font-face avant la capture.
 *
 * Astuce : on déclare la police acheminée sous le NOM EXACT utilisé dans le
 * projet (ex "Clasica Striped by Marta van Ec", éventuellement tronqué). Le
 * @font-face côté service utilisera ce même nom → le texte le retrouve, peu
 * importe que le nom diffère du family réel du fichier (résout la troncature).
 */
import { useFontStore, BASE_FONTS } from '../store/useFontStore';
import { getAllFonts } from './fontDB';

const GENERIC_FAMILIES = new Set([
    'sans-serif', 'serif', 'monospace', 'cursive', 'fantasy',
    'system-ui', 'ui-sans-serif', 'ui-serif', 'ui-monospace',
    'inherit', 'initial', 'unset', '-apple-system', 'blinkmacsystemfont',
]);

function cleanFamily(f) {
    if (!f) return null;
    const first = String(f).split(',')[0].trim().replace(/^['"]|['"]$/g, '').trim();
    if (!first || GENERIC_FAMILIES.has(first.toLowerCase())) return null;
    return first;
}

// Familles utilisées : el.font (top-level) + marks textStyle.fontFamily (rich text).
function collectUsedFamilies(project) {
    const fams = new Set();
    const fromContent = (node) => {
        if (!node || typeof node !== 'object') return;
        for (const m of (node.marks || [])) {
            if (m.type === 'textStyle' && m.attrs && m.attrs.fontFamily) {
                const c = cleanFamily(m.attrs.fontFamily);
                if (c) fams.add(c);
            }
        }
        for (const c of (node.content || [])) fromContent(c);
    };
    for (const el of (project.elements || [])) {
        const c = cleanFamily(el.font);
        if (c) fams.add(c);
        if (el.content) fromContent(el.content);
    }
    return [...fams];
}

function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onloadend = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
    });
}

// Trouve la meilleure FontData locale correspondant au nom de famille du projet.
// Matching tolérant (égalité, préfixe dans les deux sens, inclusion) pour gérer
// les noms tronqués/normalisés. Préfère la variante Regular.
function pickFontData(list, fam) {
    const low = fam.toLowerCase();
    const matches = list.filter((fd) => {
        const f = (fd.family || '').toLowerCase();
        if (!f) return false;
        return f === low || f.startsWith(low) || low.startsWith(f) || f.includes(low) || low.includes(f);
    });
    if (!matches.length) return null;
    const isRegular = (fd) => /regular|book|normal|roman/i.test(fd.style || '');
    const notStyled = (fd) => !/bold|italic|oblique|light|thin|black|heavy|medium|semib|demi|condensed|expanded/i.test(fd.style || '');
    return matches.find(isRegular) || matches.find(notStyled) || matches[0];
}

/**
 * @param {object} project - { canvas, elements, aiImage }
 * @returns {Promise<Array<{family:string, dataUrl:string}>|null>}
 */
export async function collectExportFonts(project) {
    try {
        const used = collectUsedFamilies(project);
        if (!used.length) return null;

        const out = [];
        const handled = new Set();

        // 1) Polices IMPORTÉES par l'utilisateur (.ttf/.otf en IndexedDB) : on a le
        //    fichier → data-URL direct, AUCUNE dépendance à l'OS ni à une permission.
        //    C'est le chemin fiable qui évite la substitution.
        let uploaded = [];
        try { uploaded = await getAllFonts(); } catch (_) { uploaded = []; }
        const upMap = new Map(uploaded.map((e) => [String(e.family).toLowerCase(), e]));
        for (const fam of used) {
            const key = fam.toLowerCase();
            if (handled.has(key)) continue;
            const e = upMap.get(key);
            if (!e) continue;
            try {
                const dataUrl = await blobToDataUrl(e.blob);
                out.push({ family: fam, dataUrl });
                handled.add(key);
            } catch (_) { /* skip cette police */ }
        }

        // 2) Familles connues = bundlées + Google → déjà gérées par /print (Google Fonts).
        const { googleFonts } = useFontStore.getState();
        const known = new Set([...BASE_FONTS, ...(googleFonts || [])].map((s) => s.toLowerCase()));

        // 3) Reste = polices SYSTÈME (window.queryLocalFonts, API Chrome/Edge
        //    permission-gated) — fallback historique, best-effort.
        const systemFams = used.filter((f) => !handled.has(f.toLowerCase()) && !known.has(f.toLowerCase()));
        if (systemFams.length && typeof window !== 'undefined' && ('queryLocalFonts' in window)) {
            let localData = null;
            try {
                localData = await window.queryLocalFonts();
            } catch (_) {
                localData = null; // permission refusée / non disponible
            }
            if (localData) {
                for (const fam of systemFams) {
                    const key = fam.toLowerCase();
                    if (handled.has(key)) continue;
                    const fd = pickFontData(localData, fam);
                    if (!fd) continue;
                    try {
                        const blob = await fd.blob();
                        const dataUrl = await blobToDataUrl(blob);
                        out.push({ family: fam, dataUrl });
                        handled.add(key);
                    } catch (_) { /* skip cette police */ }
                }
            }
        }

        return out.length ? out : null;
    } catch (_) {
        return null;
    }
}
